import { useState } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { ROUTES } from '@/routes/routes'
import { daysLeft } from '@/lib/trial'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'

// Once the trial has this many days (or fewer) left, the banner shows regardless of a
// prior dismissal and can't be dismissed — an ending trial shouldn't be missable.
const FORCE_SHOW_WITHIN_DAYS = 3

function readDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeDismissed(key: string) {
  try {
    localStorage.setItem(key, '1')
  } catch {
    // Storage unavailable (private mode, etc.) — the banner just won't stay dismissed
    // across route changes; acceptable.
  }
}

/** Slim bar shown across the app while a household is on its free trial. Dismissible — a
 *  dismissal is remembered per household for the rest of the trial — except in the final
 *  few days, when it's forced visible. */
export function TrialBanner({ trialEndsAt }: { trialEndsAt: string | null }) {
  const { householdId } = useCurrentHousehold()
  const key = `trialBannerDismissed:${householdId ?? 'unknown'}`

  // Re-read the stored flag if the active household changes (React's "adjust state on a
  // changing input during render" pattern — no effect needed).
  const [dismissed, setDismissed] = useState(() => readDismissed(key))
  const [seenKey, setSeenKey] = useState(key)
  if (key !== seenKey) {
    setSeenKey(key)
    setDismissed(readDismissed(key))
  }

  if (!trialEndsAt) return null

  const n = daysLeft(trialEndsAt)
  const forceShow = n <= FORCE_SHOW_WITHIN_DAYS
  if (dismissed && !forceShow) return null

  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-sm ${
        forceShow
          ? 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning-text)]'
          : 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]'
      }`}
    >
      <span>
        {n === 0
          ? 'Your free trial ends today.'
          : `${n} day${n === 1 ? '' : 's'} left in your free trial.`}
      </span>
      <Link to={ROUTES.APP.SETTINGS_BILLING} className="font-semibold underline underline-offset-2">
        Choose a plan
      </Link>
      {!forceShow && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            writeDismissed(key)
            setDismissed(true)
          }}
          className="ml-1 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}
